import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { seoLinkChecks, seoLinkCheckAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/link-check")({
  head: () => ({ meta: [{ title: "Link check · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: LinkCheckPage,
});

function LinkCheckPage() {
  const [filter, setFilter] = useState<"broken" | "all">("broken");
  const load = useServerFn(seoLinkChecks);
  const act = useServerFn(seoLinkCheckAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-link-checks", filter], queryFn: () => load({ data: { filter } }) });
  const d = q.data as any;
  const rows = (d?.rows ?? []) as any[];

  const run = useMutation({
    mutationFn: () => act({ data: { action: "run" } }),
    onSuccess: (r: any) => {
      toast.success(`Checked ${r.linksChecked} links across ${r.pagesScanned} pages — ${r.broken} broken.`);
      qc.invalidateQueries({ queryKey: ["seo-link-checks"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Link check failed."),
  });

  const recheck = useMutation({
    mutationFn: (url: string) => act({ data: { action: "recheck", url } }),
    onSuccess: (r: any) => {
      toast[r.ok ? "success" : "error"](r.ok ? "Link works now." : `Still failing (${r.status ?? "no response"}).`);
      qc.invalidateQueries({ queryKey: ["seo-link-checks"] });
    },
  });

  return (
    <SeoPage
      title="Link check"
      description="Every link this site publishes, checked on a schedule. A link that starts failing months from now is caught here before a visitor hits it."
      actions={
        <>
          <Button variant="outline" onClick={() => setFilter(filter === "broken" ? "all" : "broken")}>
            {filter === "broken" ? "Show all links" : "Show failing only"}
          </Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? "Checking…" : "Check links now"}
          </Button>
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Links tracked" value={num(d?.totalTracked ?? 0)} />
            <Stat label="Failing" value={num(rows.filter((r) => !r.ok).length)} />
            <Stat label="Last check" value={rows[0]?.last_checked_at ? new Date(rows[0].last_checked_at).toLocaleString() : "—"} />
          </div>
          <Panel title={filter === "broken" ? "Failing links" : "All links"} subtitle="Timeouts are never reported as broken — only real error responses count.">
            {rows.length === 0 ? (
              <Empty>No results yet. Run a check to build the list.</Empty>
            ) : (
              <DataTable
                head={["Link", "Type", "Status", "Found on", "Fails in a row", ""]}
                rows={rows.map((r) => [
                  <span key={r.url} className="break-all">{r.url}</span>,
                  r.kind,
                  <Pill key={`${r.url}-s`} tone={r.ok ? "good" : "bad"}>{r.ok ? `OK ${r.status_code ?? ""}` : (r.reason ?? "failing")}</Pill>,
                  (r.source_paths ?? []).slice(0, 3).join(", ") || "—",
                  r.consecutive_failures ?? 0,
                  <Button key={`${r.url}-b`} size="sm" variant="outline" onClick={() => recheck.mutate(r.url)}>
                    Check again
                  </Button>,
                ])}
              />
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
